import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  XMarkIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  EnvelopeIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  Bars2Icon
} from '@heroicons/react/24/outline';
import { db } from '../lib/cloudsql';
import { formatUnitPriceDisplay } from '../utils/formatNumberInput';
import {
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  checkGmailStatus
} from '../lib/gmailApi';

interface QuoteTerm {
  id?: string;
  name: string;
  content: string;
  is_default?: boolean; // Cloud SQL 使用 is_default 而不是 is_active
  created_at?: string;
  updated_at?: string;
}

interface PartnerSkill {
  id?: string;
  name: string;
  description?: string;
  hourly_rate?: number;
  unit?: '小時' | '計次';
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  type: 'quote' | 'invoice' | 'payment_request' | 'service_receipt' | 'collection' | 'customer_deposit';
  subject: string;
  body: string;
  variables: string[];
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'skills' | 'terms' | 'email' | 'service-rates'>('email');
  const [defaultTerms, setDefaultTerms] = useState<QuoteTerm[]>([]);
  const [partnerSkills, setPartnerSkills] = useState<PartnerSkill[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editingTerm, setEditingTerm] = useState<QuoteTerm | null>(null);
  const [editingSkill, setEditingSkill] = useState<PartnerSkill | null>(null);
  const [editingEmail, setEditingEmail] = useState<EmailTemplate | null>(null);
  const [gmailStatus, setGmailStatus] = useState<{
  api_healthy: boolean;
  gmail_configured: boolean;
} | null>(null);
  const [termFormData, setTermFormData] = useState({
    name: '',
    content: '',
    is_default: true, // Cloud SQL 使用 is_default
  });
  const [skillFormData, setSkillFormData] = useState({
    name: '',
    description: '',
    hourly_rate: undefined as number | undefined,
    unit: '小時' as '小時' | '計次',
    is_active: true,
  });
  const [emailFormData, setEmailFormData] = useState({
    name: '',
    type: 'quote' as 'quote' | 'invoice' | 'payment_request' | 'service_receipt' | 'collection' | 'customer_deposit',
    subject: '',
    body: '',
    variables: [] as string[],
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    fetchDefaultTerms();
    fetchPartnerSkills();
    fetchEmailTemplates();
    
    // 添加重試機制檢查 Gmail 狀態
    const checkGmailWithRetry = async () => {
      try {
        const status = await checkGmailStatus();
        setGmailStatus(status);
      } catch {
        // 如果失敗，延遲 2 秒後重試一次
        setTimeout(async () => {
          try {
            const status = await checkGmailStatus();
            setGmailStatus(status);
          } catch {
            // 重試失敗，設置為不可用狀態
            setGmailStatus({
              api_healthy: false,
              gmail_configured: false
            });
          }
        }, 2000);
      }
    };
    
    checkGmailWithRetry();
  }, []);

  const fetchDefaultTerms = async () => {
    try {
      // Cloud SQL 的 quote_terms 表沒有 sort_order 欄位，改用 name 排序
      const data = await db.query('quote_terms', {
        orderBy: [{ field: 'name', direction: 'asc' }]
      });
      
      setDefaultTerms((data || []) as QuoteTerm[]);
    } catch (err) {
      setError(`載入預設條款失敗: ${err instanceof Error ? err.message : '未知錯誤'}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerSkills = async () => {
    try {
      const data = await db.query('partner_skills', {
        orderBy: [{ field: 'name', direction: 'asc' }]
      });
      
      setPartnerSkills((data || []) as PartnerSkill[]);
    } catch (err) {
      setError(`載入承攬夥伴技能失敗: ${err instanceof Error ? err.message : '未知錯誤'}`);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const data = await getEmailTemplates();
      setEmailTemplates(data || []);
    } catch {
      // 載入郵件模板失敗
    }
  };



  const handleTermSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingTerm && editingTerm.id) {
        await db.update('quote_terms', editingTerm.id, termFormData);
      } else {
        await db.create('quote_terms', termFormData);
      }
      
      setShowTermsModal(false);
      setEditingTerm(null);
      resetTermForm();
      await fetchDefaultTerms();
      alert('預設條款儲存成功！');
    } catch (err) {
      alert(`儲存失敗: ${err instanceof Error ? err.message : '請檢查資料是否正確'}`);
    }
  };

  const handleSkillSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingSkill && editingSkill.id) {
        await db.update('partner_skills', editingSkill.id, skillFormData);
      } else {
        await db.create('partner_skills', skillFormData);
      }
      
      setShowSkillModal(false);
      setEditingSkill(null);
      resetSkillForm();
      await fetchPartnerSkills();
      alert('技能儲存成功！');
    } catch (err) {
      alert(`儲存失敗: ${err instanceof Error ? err.message : '請檢查資料是否正確'}`);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // 驗證編輯狀態
      const isValid = await validateEditingEmail();
      
      if (editingEmail && editingEmail.id && isValid) {
        try {
          await updateEmailTemplate(editingEmail.id, emailFormData);
        } catch {
          // 如果更新失敗，嘗試創建新記錄
          await createEmailTemplate(emailFormData);
        }
      } else {
        await createEmailTemplate(emailFormData);
      }
      
      setShowEmailModal(false);
      setEditingEmail(null);
      resetEmailForm();
      await fetchEmailTemplates();
      alert('郵件模板儲存成功！');
    } catch (err) {
      alert(`儲存失敗: ${err instanceof Error ? err.message : '請檢查資料是否正確'}`);
    }
  };


  const resetTermForm = () => {
    setTermFormData({
      name: '',
      content: '',
      is_default: true,
    });
  };

  const resetSkillForm = () => {
    setSkillFormData({
      name: '',
      description: '',
      hourly_rate: undefined,
      unit: '小時',
      is_active: true,
    });
  };

  const resetEmailForm = () => {
    setEmailFormData({
      name: '',
      type: 'quote',
      subject: '',
      body: '',
      variables: [],
      is_active: true,
      sort_order: 0
    });
  };


  const editTerm = (term: QuoteTerm) => {
    setEditingTerm(term);
    setTermFormData({
      name: term.name,
      content: term.content,
      is_default: term.is_default !== false, // Cloud SQL 使用 is_default
    });
    setShowTermsModal(true);
  };

  const editSkill = (skill: PartnerSkill) => {
    setEditingSkill(skill);
    setSkillFormData({
      name: skill.name,
      description: skill.description || '',
      hourly_rate: skill.hourly_rate,
      unit: skill.unit || '小時',
      is_active: skill.is_active,
    });
    setShowSkillModal(true);
  };

  const editEmail = (template: EmailTemplate) => {
    setEditingEmail(template);
    setEmailFormData({
      name: template.name,
      type: template.type,
      subject: template.subject,
      body: template.body,
      variables: template.variables || [],
      is_active: template.is_active,
      sort_order: template.sort_order
    });
    setShowEmailModal(true);
  };


  const deleteTerm = async (termId: string) => {
    if (!confirm('確定要刪除此條款範本嗎？')) return;
    
    try {
      await db.delete('quote_terms', termId);
      await fetchDefaultTerms();
      alert('預設條款刪除成功！');
    } catch (err) {
      alert(`刪除失敗: ${err instanceof Error ? err.message : '請稍後再試'}`);
    }
  };

  const deleteSkill = async (skillId: string) => {
    if (!confirm('確定要刪除此技能嗎？')) return;
    
    try {
      await db.delete('partner_skills', skillId);
      await fetchPartnerSkills();
      alert('技能刪除成功！');
    } catch (err) {
      alert(`刪除失敗: ${err instanceof Error ? err.message : '請稍後再試'}`);
    }
  };

  const deleteEmail = async (templateId: string) => {
    if (!confirm('確定要刪除此郵件模板嗎？')) return;
    
    try {
      await deleteEmailTemplate(templateId);
      await fetchEmailTemplates();
      alert('郵件模板刪除成功！');
    } catch (err) {
      alert(`刪除失敗: ${err instanceof Error ? err.message : '請稍後再試'}`);
    }
  };


  const createNewTerm = () => {
    setEditingTerm(null);
    resetTermForm();
    setShowTermsModal(true);
  };

  const createNewSkill = () => {
    setEditingSkill(null);
    resetSkillForm();
    setShowSkillModal(true);
  };

  const createNewEmail = () => {
    setEditingEmail(null);
    resetEmailForm();
    setShowEmailModal(true);
  };


  // 驗證編輯狀態的有效性
  const validateEditingEmail = async () => {
    if (editingEmail && editingEmail.id) {
      try {
        const data = await db.getById('email_templates', editingEmail.id);

        if (!data) {
          setEditingEmail(null);
          return false;
        }
        return true;
      } catch {
        setEditingEmail(null);
        return false;
      }
    }
    return true;
  };

  const getTypeLabel = (type: string) => {
    const typeLabels: Record<string, string> = {
      quote: '報價單',
      invoice: '發票',
      payment_request: '請款單',
      service_receipt: '勞務報酬單',
      collection: '帳務催收',
      customer_deposit: '客戶儲值金'
    };
    return typeLabels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">系統設定</h1>
            <p className="text-gray-600">管理系統基本設定</p>
          </div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">載入失敗</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
                <p className="mt-2">請檢查網路連線和後端 API 配置是否正確。</p>
                <button
                  onClick={() => {
                    setError(null);
                    setLoading(true);
                    fetchDefaultTerms();
                    fetchPartnerSkills();
                    fetchEmailTemplates();
                  }}
                  className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  重新載入
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">系統設定</h1>
          <p className="text-gray-600">管理系統基本設定</p>
        </div>
      </div>



      {/* 標籤頁 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('email')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'email'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <EnvelopeIcon className="w-5 h-5 inline mr-2" />
            郵件模板
          </button>
          <button
            onClick={() => setActiveTab('terms')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'terms'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ClipboardDocumentListIcon className="w-5 h-5 inline mr-2" />
            報價單預設條款
          </button>
          <button
            onClick={() => setActiveTab('skills')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'skills'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <WrenchScrewdriverIcon className="w-5 h-5 inline mr-2" />
            承攬夥伴技能
          </button>
          <button
            onClick={() => setActiveTab('service-rates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'service-rates'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CurrencyDollarIcon className="w-5 h-5 inline mr-2" />
            服務收費表
          </button>
        </nav>
      </div>

      {/* 承攬夥伴技能管理 */}
      {activeTab === 'skills' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">承攬夥伴技能管理</h2>
            <button
              onClick={createNewSkill}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              新增技能
            </button>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {partnerSkills.map((skill) => (
                <li key={skill.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <WrenchScrewdriverIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {skill.name}
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            skill.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {skill.is_active ? '啟用' : '停用'}
                          </span>
                            {skill.hourly_rate !== undefined && skill.hourly_rate !== null && (
                            <span className="ml-2 text-xs text-gray-600 font-normal">
                              {formatUnitPriceDisplay(skill.hourly_rate, skill.unit)} / {skill.unit || '小時'}
                            </span>
                          )}
                        </div>
                        {skill.description && (
                          <div className="text-sm text-gray-500">{skill.description}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => editSkill(skill)}
                        className="text-primary hover:text-primary-dark"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteSkill(skill.id!)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* 條款範本管理 */}
      {activeTab === 'terms' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">報價單預設條款</h2>
            <button
              onClick={createNewTerm}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              新增預設條款
            </button>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {defaultTerms.map((term) => (
                <li key={term.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <DocumentTextIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {term.name}
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            term.is_default 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {term.is_default ? '預設' : '非預設'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => editTerm(term)}
                        className="text-primary hover:text-primary-dark"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteTerm(term.id!)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* 郵件模板管理 */}
      {activeTab === 'email' && (
        <div className="space-y-6">
          {/* Gmail 狀態 */}
          {gmailStatus && (
            <div className={`p-4 rounded-lg ${gmailStatus.gmail_configured ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-3 ${gmailStatus.gmail_configured ? 'bg-green-400' : 'bg-yellow-400'}`}></div>
                <div>
                  <p className="text-sm font-medium">
                    Gmail 狀態: {gmailStatus.gmail_configured ? '已配置' : '未配置'}
                  </p>
                  <p className="text-xs text-gray-600">
                    API 服務: {gmailStatus.api_healthy ? '正常' : '異常'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">郵件模板管理</h2>
            <button
              onClick={createNewEmail}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              新增郵件模板
            </button>
          </div>

          {/* 支援的變數說明 */}
          <div className="bg-ci-secondary/20 border border-ci-secondary/40 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">支援的變數清單</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
              <div>
                <h4 className="font-medium mb-1">報價單變數</h4>
                <ul className="space-y-1 text-xs">
                  <li><code className="bg-blue-100 px-1 rounded">{'{{quote_number}}'}</code> - 報價單號</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{quote_name}}'}</code> - 報價單名稱</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{customer_name}}'}</code> - 客戶名稱</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{total_amount}}'}</code> - 總金額（整數+千分位）</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{金額}}'}</code> - 同上，可搭配「元」使用如 {'{{金額}} 元'}</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{valid_until}}'}</code> - 有效期限（年月日）</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-1">勞務報酬單變數</h4>
                <ul className="space-y-1 text-xs">
                  <li><code className="bg-blue-100 px-1 rounded">{'{{statement_number}}'}</code> - 報酬單號</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{recipient_name}}'}</code> - 收件人姓名</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{total_amount}}'}</code> - 總金額</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{period_start}}'}</code> - 期間開始</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{period_end}}'}</code> - 期間結束</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-1">客戶儲值金變數</h4>
                <ul className="space-y-1 text-xs">
                  <li><code className="bg-blue-100 px-1 rounded">{'{{deposit_id}}'}</code> - 儲值金編號</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{customer_name}}'}</code> - 客戶名稱</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{deposit_type}}'}</code> - 儲值金類型</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{amount}}'}</code> - 儲值金額</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{remaining_balance}}'}</code> - 剩餘餘額</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{payment_date}}'}</code> - 付款日期</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{project_name}}'}</code> - 專案名稱</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{bonus_percentage}}'}</code> - 優惠百分比</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{bonus_amount}}'}</code> - 優惠金額</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-1">通用變數</h4>
                <ul className="space-y-1 text-xs">
                  <li><code className="bg-blue-100 px-1 rounded">{'{{company_name}}'}</code> - 公司名稱</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{contact_name}}'}</code> - 聯絡人姓名</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{contact_email}}'}</code> - 聯絡人郵件</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{project_name}}'}</code> - 專案名稱</li>
                  <li><code className="bg-blue-100 px-1 rounded">{'{{current_date}}'}</code> - 當前日期</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-primary mt-3">
              使用方式：在郵件內容中使用 <code className="bg-blue-100 px-1 rounded">{'{{變數名稱}}'}</code> 格式，系統會自動替換為實際資料。
            </p>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {emailTemplates.map((template) => (
                <li key={template.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <EnvelopeIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {template.name}
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            template.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {template.is_active ? '啟用' : '停用'}
                          </span>
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getTypeLabel(template.type)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">{template.subject}</div>
                        <div className="text-sm text-gray-400 mt-1">
                          排序: {template.sort_order || 0}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => editEmail(template)}
                        className="text-primary hover:text-primary-dark"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteEmail(template.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}


      {/* 條款範本模態框 */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">
                {editingTerm ? '編輯預設條款' : '新增預設條款'}
              </h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleTermSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">條款名稱 *</label>
                <input
                  type="text"
                  required
                  value={termFormData.name}
                  onChange={(e) => setTermFormData({...termFormData, name: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">條款內容 *</label>
                <textarea
                  required
                  value={termFormData.content}
                  onChange={(e) => setTermFormData({...termFormData, content: e.target.value})}
                  rows={10}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="term_is_default"
                  checked={termFormData.is_default}
                  onChange={(e) => setTermFormData({...termFormData, is_default: e.target.checked})}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="term_is_default" className="ml-2 block text-sm text-gray-900">
                  設為預設條款
                </label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowTermsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                >
                  {editingTerm ? '更新' : '新增'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 技能管理模態框 */}
      {showSkillModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">
                {editingSkill ? '編輯技能' : '新增技能'}
              </h2>
              <button
                onClick={() => setShowSkillModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSkillSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">技能名稱 *</label>
                <input
                  type="text"
                  required
                  value={skillFormData.name}
                  onChange={(e) => setSkillFormData({...skillFormData, name: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">描述</label>
                <textarea
                  value={skillFormData.description}
                  onChange={(e) => setSkillFormData({...skillFormData, description: e.target.value})}
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">預設時薪/單價</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={skillFormData.hourly_rate ?? ''}
                    onChange={(e) => setSkillFormData({
                      ...skillFormData, 
                      hourly_rate: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    placeholder="選填"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <p className="mt-1 text-xs text-gray-500">用於建立合約時的預設值</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">計費單位 *</label>
                  <select
                    value={skillFormData.unit}
                    onChange={(e) => setSkillFormData({
                      ...skillFormData, 
                      unit: e.target.value as '小時' | '計次'
                    })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                  >
                    <option value="小時">小時</option>
                    <option value="計次">計次</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="skill_is_active"
                  checked={skillFormData.is_active}
                  onChange={(e) => setSkillFormData({...skillFormData, is_active: e.target.checked})}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="skill_is_active" className="ml-2 block text-sm text-gray-900">
                  啟用此技能
                </label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowSkillModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                >
                  {editingSkill ? '更新' : '新增'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 郵件模板管理模態框 */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">
                {editingEmail ? '編輯郵件模板' : '新增郵件模板'}
              </h2>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">模板名稱 *</label>
                <input
                  type="text"
                  required
                  value={emailFormData.name}
                  onChange={(e) => setEmailFormData({...emailFormData, name: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">模板類型 *</label>
                <select
                  required
                  value={emailFormData.type}
                  onChange={(e) => setEmailFormData({...emailFormData, type: e.target.value as 'quote' | 'invoice' | 'payment_request' | 'service_receipt' | 'collection' | 'customer_deposit'})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                >
                  <option value="quote">報價單</option>
                  <option value="payment_request">請款單</option>
                  <option value="service_receipt">勞務報酬單</option>
                  <option value="collection">帳務催收</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">郵件主旨 *</label>
                <input
                  type="text"
                  required
                  value={emailFormData.subject}
                  onChange={(e) => setEmailFormData({...emailFormData, subject: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">郵件內容 *</label>
                <textarea
                  required
                  value={emailFormData.body}
                  onChange={(e) => setEmailFormData({...emailFormData, body: e.target.value})}
                  rows={8}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                  placeholder="支援 HTML 格式，可使用變數如 {{customer_name}}、{{quote_number}} 等"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">排序</label>
                <input
                  type="number"
                  value={emailFormData.sort_order}
                  onChange={(e) => setEmailFormData({...emailFormData, sort_order: parseInt(e.target.value) || 0})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="email_is_active"
                  checked={emailFormData.is_active}
                  onChange={(e) => setEmailFormData({...emailFormData, is_active: e.target.checked})}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="email_is_active" className="ml-2 block text-sm text-gray-900">
                  啟用此模板
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                >
                  {editingEmail ? '更新' : '新增'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 服務收費表管理 */}
      {activeTab === 'service-rates' && (
        <ServiceRatesTab />
      )}

    </div>
  );
};

// =============================================
// 服務收費表 Tab 組件
// =============================================
interface ServiceRate {
  id?: string;
  customer_id?: string;
  service_type: string;
  hourly_rate?: number;
  unit_price?: number;
  unit?: string;
  year?: number;
  description?: string;
  is_active?: boolean;
  created_at?: string;
}

type SortColumn = 'service_type' | 'unit_price' | 'unit' | 'year';
type SortDirection = 'asc' | 'desc';

const ServiceRatesTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [serviceRates, setServiceRates] = useState<ServiceRate[]>([]);
  const [filteredRates, setFilteredRates] = useState<ServiceRate[]>([]);
  const [currentRate, setCurrentRate] = useState<ServiceRate | null>(null);
  const [showCreateRate, setShowCreateRate] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [sortBy, setSortBy] = useState<SortColumn>('service_type');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    fetchServiceRates();
    setLoading(false);
  }, []);

  useEffect(() => {
    if (selectedYear === 0) {
      setFilteredRates(serviceRates);
    } else {
      setFilteredRates(serviceRates.filter(rate => rate.year && rate.year === selectedYear));
    }
  }, [serviceRates, selectedYear]);

  const sortedRates = [...filteredRates].sort((a, b) => {
    let aVal: string | number | undefined;
    let bVal: string | number | undefined;
    switch (sortBy) {
      case 'service_type':
        aVal = (a.service_type || '').toLowerCase();
        bVal = (b.service_type || '').toLowerCase();
        break;
      case 'unit_price':
        aVal = a.unit_price ?? a.hourly_rate ?? 0;
        bVal = b.unit_price ?? b.hourly_rate ?? 0;
        break;
      case 'unit':
        aVal = (a.unit || '').toLowerCase();
        bVal = (b.unit || '').toLowerCase();
        break;
      case 'year':
        aVal = a.year ?? 0;
        bVal = b.year ?? 0;
        break;
      default:
        return 0;
    }
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortDirection === 'asc' ? cmp : -cmp;
  });

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const SortHeader: React.FC<{
    column: SortColumn;
    label: string;
  }> = ({ column, label }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 rounded-t"
      onClick={() => handleSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortBy === column ? (
          sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )
        ) : (
          <Bars2Icon className="h-4 w-4 opacity-40" />
        )}
      </span>
    </th>
  );

  const fetchServiceRates = async () => {
    try {
      const data = await db.query<ServiceRate>('service_rates', {
        orderBy: [{ field: 'created_at', direction: 'desc' }]
      });
      const mappedData = (data || []).map(rate => {
        let unit = '小時';
        let year = new Date().getFullYear();
        if (rate.description) {
          const unitMatch = rate.description.match(/單位:\s*([^|]+)/);
          if (unitMatch) {
            unit = unitMatch[1].trim();
          }
          const yearMatch = rate.description.match(/年份:\s*(\d+)/);
          if (yearMatch) {
            year = parseInt(yearMatch[1]);
          }
        }
        return {
          ...rate,
          unit_price: rate.hourly_rate || rate.unit_price || 0,
          unit,
          year
        };
      });
      setServiceRates(mappedData);
    } catch (error) {
      console.error('取得服務費率失敗:', error);
    }
  };

  const getYearOptions = () => {
    const years = Array.from(new Set(
      serviceRates
        .map(rate => rate.year)
        .filter((year): year is number => year !== undefined && year !== null && typeof year === 'number')
    )).sort((a, b) => b - a);
    return years;
  };

  const copyPreviousYearRates = async () => {
    const currentYear = new Date().getFullYear();
    const targetYear = selectedYear === 0 ? currentYear : selectedYear;
    const sourceYear = targetYear - 1;
    const prevYearRates = serviceRates.filter(rate => rate.year === sourceYear);
    if (prevYearRates.length === 0) {
      alert(`前一年度（${sourceYear} 年）尚無收費項目可複製`);
      return;
    }
    const targetYearRates = serviceRates.filter(rate => rate.year === targetYear);
    const targetServiceTypes = new Set(targetYearRates.map(r => r.service_type));
    const toCopy = prevYearRates.filter(r => !targetServiceTypes.has(r.service_type));
    if (toCopy.length === 0) {
      alert(`${targetYear} 年已包含前一年度的所有收費項目，無需複製`);
      return;
    }
    if (!confirm(`確定要將 ${sourceYear} 年的 ${toCopy.length} 筆收費項目複製到 ${targetYear} 年嗎？`)) return;
    try {
      for (const rate of toCopy) {
        const price = rate.unit_price || rate.hourly_rate || 0;
        const unit = rate.unit || '小時';
        const description = `單位: ${unit} | 年份: ${targetYear}`;
        await db.create('service_rates', {
          service_type: rate.service_type,
          hourly_rate: price,
          description,
          is_active: true
        });
      }
      fetchServiceRates();
      setSelectedYear(targetYear);
      alert(`已成功複製 ${toCopy.length} 筆收費項目至 ${targetYear} 年`);
    } catch (error) {
      console.error('複製前一年度資料失敗:', error);
      alert('複製失敗，請稍後再試');
    }
  };

  const createNewRate = () => {
    setCurrentRate({
      service_type: '',
      unit_price: 0,
      unit: '小時',
      year: selectedYear === 0 ? new Date().getFullYear() : selectedYear
    });
    setIsEditing(false);
    setShowCreateRate(true);
  };

  const editRate = (rate: ServiceRate) => {
    setCurrentRate({ ...rate });
    setIsEditing(true);
    setShowCreateRate(true);
  };

  const copyRate = (rate: ServiceRate) => {
    setCurrentRate({
      service_type: `${rate.service_type} (複製)`,
      unit_price: rate.unit_price,
      unit: rate.unit,
      year: rate.year
    });
    setIsEditing(false);
    setShowCreateRate(true);
  };

  const saveRate = async () => {
    if (!currentRate) return;
    
    try {
      const price = currentRate.unit_price || currentRate.hourly_rate || 0;
      const unit = currentRate.unit || '小時';
      const year = currentRate.year || new Date().getFullYear();
      const description = `單位: ${unit} | 年份: ${year}`;
      
      if (isEditing) {
        await db.update('service_rates', currentRate.id!, {
          service_type: currentRate.service_type,
          hourly_rate: price,
          description,
          is_active: currentRate.is_active !== false
        });
      } else {
        const newRate = {
          service_type: currentRate.service_type,
          hourly_rate: price,
          description,
          is_active: true
        };
        
        await db.create('service_rates', newRate);
      }
      
      setShowCreateRate(false);
      setCurrentRate(null);
      setIsEditing(false);
      fetchServiceRates();
      alert('儲存成功！');
    } catch {
      alert('儲存失敗，請檢查資料是否正確');
    }
  };

  const handleDeleteRate = async (rate: ServiceRate) => {
    if (!confirm('確定要刪除此收費項目嗎？')) return;
    
    try {
      if (rate.id) {
        await db.delete('service_rates', rate.id);
        fetchServiceRates();
        alert('刪除成功！');
      }
    } catch {
      alert('刪除失敗，請稍後再試');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">服務收費表管理</h2>
        <button
          onClick={createNewRate}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          新增收費項目
        </button>
      </div>

      {/* 年度篩選 */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">篩選年度：</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value) || 0)}
              className="border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
            >
              <option key="all" value={0}>全部年度</option>
              {getYearOptions().map(year => (
                <option key={`year-${year}`} value={year}>{year} 年</option>
              ))}
            </select>
            <span className="text-sm text-gray-500">
              共 {filteredRates.length} 筆資料
            </span>
          </div>
          <button
            type="button"
            onClick={copyPreviousYearRates}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
            複製前一年度資料
          </button>
        </div>
      </div>

      {/* 收費項目表格 */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">收費項目列表</h3>
          {filteredRates.length === 0 ? (
            <div className="text-center py-8">
              <CurrencyDollarIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">尚未建立任何收費項目</p>
              <p className="text-sm text-gray-400 mt-1">點擊「新增收費項目」開始建立</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortHeader column="service_type" label="收費項目" />
                    <SortHeader column="unit_price" label="單價" />
                    <SortHeader column="unit" label="單位" />
                    <SortHeader column="year" label="年度" />
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedRates.map((rate) => (
                    <tr key={rate.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {rate.service_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatUnitPriceDisplay(rate.unit_price ?? rate.hourly_rate ?? 0, rate.unit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {rate.unit || '小時'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {rate.year || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          className="text-primary hover:text-primary-dark mr-3"
                          onClick={e => { e.stopPropagation(); copyRate(rate); }}
                          title="複製"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>
                        <button
                          className="text-primary hover:text-primary-dark mr-3"
                          onClick={e => { e.stopPropagation(); editRate(rate); }}
                          title="編輯"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-900"
                          onClick={e => { e.stopPropagation(); handleDeleteRate(rate); }}
                          title="刪除"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 新增收費項目模態框 */}
      {showCreateRate && currentRate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40" />
          <div className="relative z-50 w-96 bg-white rounded-md shadow-lg border p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {isEditing ? '編輯收費項目' : currentRate.service_type.includes('(複製)') ? '複製收費項目' : '新增收費項目'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateRate(false);
                  setCurrentRate(null);
                  setIsEditing(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">收費項目 *</label>
                <input
                  type="text"
                  required
                  value={currentRate.service_type}
                  onChange={(e) => setCurrentRate({...currentRate, service_type: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">單價 *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={currentRate.unit_price || ''}
                  onChange={(e) => setCurrentRate({...currentRate, unit_price: parseFloat(e.target.value) || 0})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">單位 *</label>
                <input
                  type="text"
                  required
                  value={currentRate.unit || '小時'}
                  onChange={(e) => setCurrentRate({...currentRate, unit: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                  placeholder="小時、計次等"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">年度 *</label>
                <input
                  type="number"
                  required
                  value={currentRate.year || new Date().getFullYear()}
                  onChange={(e) => setCurrentRate({...currentRate, year: parseInt(e.target.value) || new Date().getFullYear()})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowCreateRate(false);
                    setCurrentRate(null);
                    setIsEditing(false);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={saveRate}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                >
                  儲存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 